import React, { lazy, useEffect, useRef, useState } from 'react'
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom'

import { InterviewCallModal } from './modals/InterviewCallModal';
import { JSBoothDetail } from './components/JSBoothDetail'
import JSHistory from './components/JSHistory';
const JSInterview = lazy(() => import('./components/JSInterview'));
import JSLobby from './components/JSLobby'
import { setupAmplifyMessageSubscription } from 'app/amplify-subscriptions';
import { useAuth } from 'app/context/auth-context';
import { useAppContext } from 'app/context/app-context';
import { AppModal } from '../shared/modals/AppModal';
import { EmptyGuid, jobSeekerUrl } from 'app/types';
import { Notifications } from 'app/modules/shared';
import { UrlParamReplace, getTimezoneOffset } from 'app/helpers/MiscUtils';
import { VideoSessionStatus } from 'app/API';
import useIsMounted from 'app/hooks/useIsMounted';
import { HMSRoomProvider } from '@100mslive/react-sdk';
import { RxSubscription } from 'app/RxSubscriptions';
import { ManageProfile } from '../shared/account/manage-profile';
import JobFairAuthComponent from './JobfairAuth';
import { JobfairService } from 'app/services/JobfairService';
import { JobFairStatus } from 'app/types';
import { VJFPUserService } from 'app/services/VJFPUserService';

const isProfilePath = (pathname: string) => {
  // Match pattern: /job-seeker/organizations/*/jobfairs/*/Profile
  return /\/job-seeker\/organizations\/[^/]+\/jobfairs\/[^/]+\/profile/.test(pathname);
}

export const JSRoutes = {
  Lobby: `${jobSeekerUrl}/lobby`,
  BoothDetail: `${jobSeekerUrl}/lobby/booth/:boothId`,
  DeviceTest: `${jobSeekerUrl}/device-test`,
  Interview: `${jobSeekerUrl}/interview/:sessionId`,
  RateCompany: `${jobSeekerUrl}/interview/:sessionId/rate-company`,
  History: `${jobSeekerUrl}/history`,
  Profile: `${jobSeekerUrl}/profile`,
  Auth: jobSeekerUrl,
}

const jobseekerBreadCrumbs = [{
  text: 'Lobby',
  url: UrlParamReplace(JSRoutes.Lobby, '', '')
}
];


interface JSPageHOCProps {
  children: React.ReactNode;
  withContinuePopup?: boolean;
}

const JSPageHOC = (hocProps: JSPageHOCProps) => {

  const { user } = useAuth();
  const history = useHistory();
  const location = useLocation()
  const [showContinuePopup, setShowContinuePopup] = useState(false);
  const store = useRef<{ interviewSessionId?: string }>({ interviewSessionId: undefined });

  const isMounted = useIsMounted('JSPage');
  const params = useParams<{ orgId: string, jobFairId: string }>();
  const { jobFair, setJobFair } = useAppContext();
  const pollingTimerRef = useRef<NodeJS.Timeout>();

  const checkAndUpdateJobFairStatus = async () => {
    if (!jobFair?.id || !isMounted()) return;

    try {
      const jobFairService = new JobfairService();
      const updatedJobFair = await jobFairService.applyStatus(jobFair);
      if (updatedJobFair && updatedJobFair.status !== jobFair.status && isMounted()) {
        // Status changed, update context
        setJobFair(updatedJobFair);
      }
    } catch (error) {
      console.error('Error checking job fair status:', error);
    }
  };

  const getPollingInterval = (fair: any) => {
    if (!fair) return 60000; // Default 1 minute

    // Calculate time until start
    const timeZoneOffset = getTimezoneOffset(fair.timezone);
    const moment = require('moment');
    const startTime = moment(`${fair.startDate} ${fair.startTime} ${timeZoneOffset}`, 'YYYY-MM-DD HH:mm ZZ').utc();
    const now = moment().utc();
    const minutesUntilStart = startTime.diff(now, 'minutes');

    if (minutesUntilStart <= 0) {
      // Job fair has started or is active
      return 30000; // Check every 30 seconds while active
    } else if (minutesUntilStart <= 5) {
      // Within 5 minutes of start
      return 10000; // Check every 10 seconds
    } else if (minutesUntilStart <= 30) {
      // Within 30 minutes of start
      return 30000; // Check every 30 seconds
    } else {
      // More than 30 minutes until start
      return 60000; // Check every minute
    }
  };

  useEffect(() => {
    if (jobFair === undefined && params.orgId && params.jobFairId) {
      new JobfairService().getJobFairById(params.orgId, params.jobFairId).then(jobFair => {
        if (!isMounted()) return;
        setJobFair(jobFair);
      }).catch(err => {
      });
      if (user?.id && !isProfilePath(location.pathname)) {
        new VJFPUserService().getById(EmptyGuid, user.id).then(vjfpUser => {
          if (vjfpUser?.isProfileComplete == null) {
            history.push(UrlParamReplace(JSRoutes.Profile));
          }
        })
          .catch(err => {
            console.log(err);
          })
      }
    }
    else {
    }
    return () => {
      // Cleanup polling timer when component unmounts
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
    }
  }, [params, jobFair, setJobFair, user?.id])

  // Status polling effect
  useEffect(() => {
    if (!jobFair) return;

    // Clear any existing timer
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }

    // Only poll if status is still Upcoming (don't poll for active or ended fairs as frequently)
    if (jobFair.status === JobFairStatus.Upcoming) {
      const interval = getPollingInterval(jobFair);
      pollingTimerRef.current = setInterval(checkAndUpdateJobFairStatus, interval);

      // Do initial check immediately
      checkAndUpdateJobFairStatus();
    }

    return () => {
      if (pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
      }
    };
  }, [jobFair?.id, jobFair?.status])


  useEffect(() => {
    if (!user?.id || !jobFair?.id)
      return;
    let timeOutRef: NodeJS.Timeout;
    const messageSubscriptions = setupAmplifyMessageSubscription(`${jobFair.id}_Applicant`);
    const subscription = RxSubscription.onChangeInterviewSessionByCandidateIdObservable().subscribe(changeEvent => {
      if (!isMounted()) return;
      const _interviewSessionId = changeEvent?.value?.id;
      if (!_interviewSessionId)
        return;
      store.current.interviewSessionId = _interviewSessionId;
      let path = UrlParamReplace(JSRoutes.Interview);
      path = UrlParamReplace(path, ':sessionId', _interviewSessionId);
      if (timeOutRef) clearTimeout(timeOutRef);
      timeOutRef = setTimeout(() => {
        const _newShowContinuePopup = changeEvent?.value?.status === VideoSessionStatus.IN_PROGRESS && location.pathname !== path;
        if (showContinuePopup !== _newShowContinuePopup)
          setShowContinuePopup(_newShowContinuePopup);
      }, 3000);
    });

    return () => {
      subscription.unsubscribe();
      if (messageSubscriptions) messageSubscriptions.dispose();
      //if (createInterviewSessionSubscription) createInterviewSessionSubscription.unsubscribe();
      if (timeOutRef) clearTimeout(timeOutRef);
    }
  }, [user?.id, jobFair?.id, isMounted])

  const onContinueSessionButtonClick = () => {
    history.push(UrlParamReplace(JSRoutes.Interview + '?startVideo=true&startAudio=true', ':sessionId', store?.current?.interviewSessionId || ''))
    setShowContinuePopup(false);
  };

  return (
    <HMSRoomProvider>
      <Notifications></Notifications>
      <InterviewCallModal />
      {hocProps.withContinuePopup !== false && <AppModal disableRejectBtn={true} variant="PlainText" successText="Continue session" rejectText="Cancel" show={showContinuePopup} title="Alert"
        onSuccess={onContinueSessionButtonClick}
        onReject={async () => { }}
      >
        You are still in an interview, please click "Continue session".
      </AppModal>}
      {hocProps.children}
    </HMSRoomProvider>
  );
}

const JSPage: React.FC = () => {
  const params = useParams<{ orgId: string, jobFairId: string }>();

  return (
    <>
      <Switch>
        <Route path={JSRoutes.BoothDetail} render={() => <JSPageHOC><JSBoothDetail orgID={params.orgId} title="" breadcrumbs={[...jobseekerBreadCrumbs, { text: 'Booth Detail' }]} /></JSPageHOC>} />
        <Route path={JSRoutes.Lobby} render={() => <JSPageHOC><JSLobby title="Event Lobby" /></JSPageHOC>} />
        <Route path={JSRoutes.Interview} render={() =>
          <JSPageHOC withContinuePopup={false}>
            <JSInterview title="Interview" breadcrumbs={[...jobseekerBreadCrumbs, { text: 'Interview' }]} />
          </JSPageHOC>} />
        <Route path={JSRoutes.History} render={() => <JSPageHOC><JSHistory title="History" breadcrumbs={[...jobseekerBreadCrumbs, { text: 'History' }]} /></JSPageHOC>} />
        <Route path={JSRoutes.Profile} render={() => <JSPageHOC><ManageProfile title="Profile" breadcrumbs={[...jobseekerBreadCrumbs, { text: 'Profile' }]} /></JSPageHOC>} />
        <Route path={JSRoutes.Auth} render={() => <JobFairAuthComponent />} />
        <Redirect from={jobSeekerUrl} to={JSRoutes.Auth} />
      </Switch>
    </>
  )
}

export default JSPage
